import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Section, ColorInput, ImageUpload, TextField, TextAreaField } from "@/components/branding/BrandingForm";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Save, RotateCcw } from "lucide-react";

const EMPTY = {
  site_title: "", school_name: "", tagline: "", primary_color: "", secondary_color: "", accent_color: "",
  logo_url: "", favicon_url: "", hero_image_url: "", landing_bg_url: "", pdf_logo_url: "", contact_email: "", phone: "", address: "",
  website_url: "", twitter_url: "", instagram_url: "", facebook_url: "", youtube_url: "",
  conference: "", district: "", mascot: "", season: "", about: "", seo_description: "",
  announcement: "", announcement_active: false,
};

export default function SiteSettingsPage() {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [initial, setInitial] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/settings");
    const merged = { ...EMPTY, ...data };
    setForm(merged); setInitial(merged);
  };
  useEffect(() => { load(); }, []);

  if (user === undefined) return <div className="p-8">Loading...</div>;
  if (!user || !user.permissions?.manage_site_settings) return <Navigate to="/dashboard" replace />;

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/settings", form);
      const merged = { ...EMPTY, ...data };
      setForm(merged); setInitial(merged);
      toast.success("Site settings saved");
      // trigger a light refresh signal so theme + header pick up new values
      window.dispatchEvent(new Event("settings-updated"));
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <main className="max-w-6xl mx-auto px-4 py-6" data-testid="site-settings-page">
      <div className="mb-4 border-b-2 border-[#0A1128] pb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-overline text-xs text-[#E11D48]">Administration</p>
          <h1 className="font-heading text-3xl tracking-tight">SITE SETTINGS</h1>
          <p className="text-sm text-slate-600 mt-1">Global branding, contact info, and site chrome. All fields are optional.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-sm" disabled={!dirty} onClick={() => setForm(initial)} data-testid="reset-btn">
            <RotateCcw className="w-4 h-4 mr-1"/>Reset
          </Button>
          <Button className="rounded-sm bg-[#0A1128] hover:bg-slate-800 font-heading" disabled={!dirty || saving} onClick={save} data-testid="save-settings-btn">
            <Save className="w-4 h-4 mr-1"/>{saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        <Section title="Identity" subtitle="Public-facing name and taglines.">
          <TextField label="Site title (browser tab)" value={form.site_title} onChange={v => set("site_title", v)} placeholder="Sports Record Book" testid="input-site-title"/>
          <TextField label="School / Association name" value={form.school_name} onChange={v => set("school_name", v)} placeholder="Lincoln High School Athletics" testid="input-school-name"/>
          <TextField label="Tagline / motto" value={form.tagline} onChange={v => set("tagline", v)} placeholder="Home of the Tigers" testid="input-tagline"/>
          <TextField label="Mascot" value={form.mascot} onChange={v => set("mascot", v)} placeholder="Tigers" testid="input-mascot"/>
          <TextField label="Conference" value={form.conference} onChange={v => set("conference", v)} placeholder="Region 4 Athletics" testid="input-conference"/>
          <TextField label="District" value={form.district} onChange={v => set("district", v)} placeholder="Metro Public Schools" testid="input-district"/>
          <TextField label="Current season" value={form.season} onChange={v => set("season", v)} placeholder="2025-2026 Season" testid="input-season" helpText="Displays prominently on sport pages."/>
          <TextAreaField label="About" value={form.about} onChange={v => set("about", v)} placeholder="Short story of your athletics program..." testid="input-about"/>
        </Section>

        <Section title="Brand Colors" subtitle="Applied to headers, badges, and sport pages that don't override them.">
          <ColorInput label="Primary color" value={form.primary_color} onChange={v => set("primary_color", v)} testid="input-primary-color"/>
          <ColorInput label="Secondary color" value={form.secondary_color} onChange={v => set("secondary_color", v)} testid="input-secondary-color"/>
          <ColorInput label="Accent color (highlights)" value={form.accent_color} onChange={v => set("accent_color", v)} testid="input-accent-color"/>
        </Section>

        <Section title="Logos & Imagery" subtitle="Upload an image or paste a URL. Max 200KB for logo/favicon.">
          <ImageUpload label="Logo" value={form.logo_url} onChange={v => set("logo_url", v)} hint="Shown in the header. PNG/SVG recommended." testid="upload-logo"/>
          <ImageUpload label="Favicon" value={form.favicon_url} onChange={v => set("favicon_url", v)} hint="Browser tab icon. 32x32 or 64x64 PNG." testid="upload-favicon" maxKb={100}/>
          <div className="md:col-span-2">
            <ImageUpload label="PDF Branding Logo" value={form.pdf_logo_url} onChange={v => set("pdf_logo_url", v)} hint="Appears at the top of downloaded PDFs. Use a horizontal PNG or JPG (recommended: transparent background)." testid="upload-pdf-logo" maxKb={400}/>
          </div>
          <div className="md:col-span-2">
            <ImageUpload label="Hero image" value={form.hero_image_url} onChange={v => set("hero_image_url", v)} hint="Large banner on the home page. Wide URL preferred over base64." testid="upload-hero" maxKb={800}/>
          </div>
          <div className="md:col-span-2">
            <ImageUpload label="Landing page background" value={form.landing_bg_url} onChange={v => set("landing_bg_url", v)} hint="Full-bleed background behind the hero on the home page. Upload an image or paste a URL. Leave blank to keep the default dark gradient." testid="upload-landing-bg" maxKb={1500}/>
          </div>
        </Section>

        <Section title="Contact & Social" subtitle="Shown in the public footer and Contact link.">
          <TextField label="Contact email" value={form.contact_email} onChange={v => set("contact_email", v)} placeholder="athletics@school.edu" testid="input-contact-email" type="email"/>
          <TextField label="Phone" value={form.phone} onChange={v => set("phone", v)} placeholder="(555) 123-4567" testid="input-phone"/>
          <TextField label="Address" value={form.address} onChange={v => set("address", v)} placeholder="123 School Rd, City, ST" testid="input-address"/>
          <TextField label="Website" value={form.website_url} onChange={v => set("website_url", v)} placeholder="https://school.edu" testid="input-website"/>
          <TextField label="Twitter / X" value={form.twitter_url} onChange={v => set("twitter_url", v)} placeholder="https://x.com/school_athletics" testid="input-twitter"/>
          <TextField label="Instagram" value={form.instagram_url} onChange={v => set("instagram_url", v)} placeholder="https://instagram.com/school_athletics" testid="input-instagram"/>
          <TextField label="Facebook" value={form.facebook_url} onChange={v => set("facebook_url", v)} placeholder="https://facebook.com/schoolathletics" testid="input-facebook"/>
          <TextField label="YouTube" value={form.youtube_url} onChange={v => set("youtube_url", v)} placeholder="https://youtube.com/@schoolathletics" testid="input-youtube"/>
        </Section>

        <Section title="Announcement Banner" subtitle="Optional site-wide message.">
          <div className="md:col-span-2">
            <TextField label="Message" value={form.announcement} onChange={v => set("announcement", v)} placeholder="🏆 State Championship game this Friday at 7pm!" testid="input-announcement"/>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="ann-active" checked={form.announcement_active} onCheckedChange={v => set("announcement_active", !!v)} data-testid="input-announcement-active"/>
            <Label htmlFor="ann-active" className="cursor-pointer text-sm">Show banner to visitors</Label>
          </div>
        </Section>

        <Section title="SEO" subtitle="Metadata used when the site is shared.">
          <div className="md:col-span-2">
            <TextAreaField label="Meta description" value={form.seo_description} onChange={v => set("seo_description", v)} placeholder="Historic record book for Lincoln High Athletics — every season, every record." testid="input-seo"/>
          </div>
        </Section>
      </div>
    </main>
  );
}
